import { IVideo } from '../../types';
import type { PickYourFiDeviceKey } from './HardwareFirstBelowFold';

const BASE = '/product_images/videos';

/**
 * Each tracker's film in full, shown twice on the page: as the last gallery slide and as a
 * below-the-fold module. Cut from the campaign masters Armand supplied, at 720p:
 *   mini  -> Mini_Relaunch_Colors_16X9_0814_R4-V3   30s
 *   s3    -> 250519_FI_DOGCOLLAR_60s_1080p_16x9     60s
 *   ultra -> Fi-2026-UnleashTheWild-60-16x9-Master  60s
 *
 * They keep their audio tracks but play muted - the module's scrim hands over real controls, and
 * unmuting is the viewer's to do. Nothing on this page ever makes noise on its own.
 *
 * 1440x810 rather than 720p because the module renders 820 css px, which is ~1640 device px on a
 * 2x screen - 1280 was being upscaled and read soft. 1440 at crf 34 lands within a hair of
 * 1600 at crf 29 on screen while costing a bit over half as much.
 *
 * `preload: 'auto'` per Alex, so they are running by the time the shopper reaches them. Note the
 * browser streams rather than swallowing the file: sitting on the page pulls a few MB, not the
 * whole 9MB, and only for the tracker being viewed.
 */
function deviceVideo(key: 'mini' | 's3' | 'ultra'): Omit<IVideo, 'showPlayScrim'> {
  return {
    type: 'video',
    url: `${BASE}/pdp-video-${key}.mp4`,
    poster: `${BASE}/pdp-video-${key}.jpg`,
    thumbnail: { type: 'image', url: `${BASE}/pdp-video-${key}.jpg` },
    preload: 'auto',
  };
}

const VIDEOS: Record<PickYourFiDeviceKey, ReturnType<typeof deviceVideo>> = {
  mini: deviceVideo('mini'),
  s3: deviceVideo('s3'),
  ultra: deviceVideo('ultra'),
};

/** The gallery slide: bare, because landing on the slide is already the ask. */
export function gallerySlideVideo(deviceKey: PickYourFiDeviceKey): IVideo {
  return VIDEOS[deviceKey];
}

/** The below-the-fold module: same loop, with a scrim offering real controls over the top. */
export function moduleVideo(deviceKey: PickYourFiDeviceKey): IVideo {
  return { ...VIDEOS[deviceKey], showPlayScrim: true };
}
